import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import type { ClientSummary } from '../types';
import { Avatar } from '../components/Avatar';

type ViewMode = 'grid' | 'list';
const VIEW_KEY = 'lexcrm.clients.view';
function readView(): ViewMode {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    return v === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
}

type ListResponse = { items: ClientSummary[]; nextCursor: string | null };

export function Clients({
  onAddClient,
  onOpen,
  searchQuery,
}: {
  onAddClient: () => void;
  onOpen: (id: string) => void;
  searchQuery: string;
}) {
  const { t } = useTranslation(['clients', 'common']);
  const [clients, setClients] = useState<ClientSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>(readView);
  // Bumps each time the active query changes; lets us ignore stale responses.
  const requestId = useRef(0);

  function setAndPersistView(v: ViewMode) {
    setView(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* ignore quota */ }
  }

  // Debounced server-side search whenever searchQuery changes
  useEffect(() => {
    const myReq = ++requestId.current;
    const tHandle = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (searchQuery.trim()) params.set('q', searchQuery.trim());
        const data = await apiFetch<ListResponse>(`/api/clients?${params}`);
        if (myReq !== requestId.current) return; // stale
        setClients(data.items);
        setNextCursor(data.nextCursor);
      } catch (e) {
        if (myReq !== requestId.current) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (myReq === requestId.current) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(tHandle);
  }, [searchQuery]);

  async function loadMore() {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery.trim()) params.set('q', searchQuery.trim());
      params.set('cursor', nextCursor);
      const data = await apiFetch<ListResponse>(`/api/clients?${params}`);
      setClients((prev) => [...prev, ...data.items]);
      setNextCursor(data.nextCursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingMore(false);
    }
  }

  if (loading && clients.length === 0) return <div className="empty">{t('list.loading')}</div>;
  if (error) return <div className="empty">{t('common:errors.with_message', { message: error })}</div>;

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 6,
          marginBottom: 10,
        }}
      >
        <button
          className={`type-btn ${view === 'grid' ? 'active' : ''}`}
          style={{ flex: 'unset', padding: '6px 12px' }}
          onClick={() => setAndPersistView('grid')}
          aria-pressed={view === 'grid'}
        >
          ⊞ {t('list.view_grid')}
        </button>
        <button
          className={`type-btn ${view === 'list' ? 'active' : ''}`}
          style={{ flex: 'unset', padding: '6px 12px' }}
          onClick={() => setAndPersistView('list')}
          aria-pressed={view === 'list'}
        >
          ☰ {t('list.view_list')}
        </button>
      </div>

      {view === 'grid' ? (
        <div className="client-grid">
          {clients.map((c) => (
            <div key={c.id} className="client-card" onClick={() => onOpen(c.id)}>
              <div className="client-card-top">
                <Avatar name={c.name} size="md" />
                <div>
                  <div className="client-name-lg">{c.name}</div>
                  <div className="client-type">{t(`types.${c.type}`)}</div>
                </div>
              </div>
              <div className="client-stats">
                <div className="client-stat">
                  <strong>{c.caseCount}</strong> {t('list.case_noun', { count: c.caseCount })}
                </div>
                {c.phone && <div className="client-stat">{c.phone}</div>}
              </div>
            </div>
          ))}
          {clients.length === 0 && searchQuery.trim() && (
            <div className="empty" style={{ gridColumn: '1 / -1' }}>
              {t('list.empty_search')}
            </div>
          )}
          <div className="client-card add-card" onClick={onAddClient}>
            {t('list.add_card')}
          </div>
        </div>
      ) : (
        <div className="full-panel">
          {clients.map((c) => (
            <div
              key={c.id}
              className="case-row"
              style={{ cursor: 'pointer' }}
              onClick={() => onOpen(c.id)}
            >
              <Avatar name={c.name} size="sm" />
              <div className="case-info">
                <div className="case-name">{c.name}</div>
                <div className="case-sub">
                  {[t(`types.${c.type}`), c.phone, c.email].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                <strong>{c.caseCount}</strong> {t('list.case_noun', { count: c.caseCount })}
              </div>
            </div>
          ))}
          {clients.length === 0 && searchQuery.trim() && (
            <div className="empty">{t('list.empty_search')}</div>
          )}
          <div
            className="case-row"
            style={{ cursor: 'pointer', justifyContent: 'center' }}
            onClick={onAddClient}
          >
            {t('list.add_card')}
          </div>
        </div>
      )}

      {nextCursor && (
        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <button className="btn-cancel" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? t('common:actions.loading_more') : t('common:actions.load_more')}
          </button>
        </div>
      )}
    </>
  );
}
